import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { 
  Users, TrendingUp, Wallet, ShieldCheck, 
  FileCheck, AlertCircle, ArrowUpRight, ArrowDownRight,
  BookOpen, ArrowUp, ArrowDown, Clock, X, Search, Calendar as CalendarIcon, ChevronDown, Phone
} from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Area, AreaChart
} from 'recharts';
import { DealerFocusClientsWidget, PrincipalFocusClientsWidget } from '../components/focus-clients/FocusClientsWidget';
import { TierOverviewWidget, DealerTierTabsWidget } from '../components/tier/TierWidgets';
import FocusSnapshotWidget from '../components/FocusSnapshotWidget';
import PrincipalBrokerageGapWidget from '../components/PrincipalBrokerageGapWidget';
import MyIncentivesWidget from '../components/MyIncentivesWidget';
import IncentivesSummaryWidget from '../components/IncentivesSummaryWidget';
import { ClickToCall } from '../components/ClickToCall';

import { API_URL } from '../utils/apiUrl';

// =============================================================================
// TIME FILTER CONFIGURATION
// =============================================================================
const TIME_FILTERS = [
  { value: 'last_trading_day', label: 'Last Trading Day' },
  { value: 'this_week', label: 'This Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_quarter', label: 'Last Quarter' },
  { value: 'this_year', label: 'This Year' },
  { value: 'custom', label: 'Custom Range' }
];

// Dynamic label mapping based on time filter
const getWidgetLabels = (timeFilter, customRange = null) => {
  // For custom range, show the date range in labels
  if (timeFilter === 'custom' && customRange?.from && customRange?.to) {
    const fromStr = new Date(customRange.from).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
    const toStr = new Date(customRange.to).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
    const rangeLabel = `${fromStr} - ${toStr}`;
    return {
      aum: 'AUM (Custom)',
      clients: `New Clients (${rangeLabel})`,
      revenue: `Revenue (${rangeLabel})`,
      active: `Active Clients (${rangeLabel})`,
      kyc: `KYC Verified (${rangeLabel})`
    };
  }
  
  const labels = {
    last_trading_day: {
      aum: 'AUM (Last Trading Day)',
      clients: 'New Clients (Last Trading Day)',
      revenue: 'Revenue (Last Trading Day)',
      active: 'Active Clients (Last Trading Day)',
      kyc: 'KYC Verified (Last Trading Day)'
    },
    this_week: {
      aum: 'AUM This Week',
      clients: 'New Clients This Week',
      revenue: 'Revenue This Week',
      active: 'Active Clients (This Week)',
      kyc: 'KYC Verified This Week'
    },
    this_month: {
      aum: 'AUM This Month',
      clients: 'New Clients This Month',
      revenue: 'Revenue This Month',
      active: 'Active Clients (This Month)',
      kyc: 'KYC Verified This Month'
    },
    last_quarter: {
      aum: 'AUM Last Quarter',
      clients: 'New Clients Last Quarter',
      revenue: 'Revenue Last Quarter',
      active: 'Active Clients (Quarter)',
      kyc: 'KYC Verified Last Quarter'
    },
    this_year: {
      aum: 'AUM YTD',
      clients: 'New Clients YTD',
      revenue: 'Revenue YTD',
      active: 'Active Clients (FY)',
      kyc: 'KYC Verified YTD'
    },
    custom: {
      aum: 'AUM (Custom)',
      clients: 'New Clients (Custom)',
      revenue: 'Revenue (Custom)',
      active: 'Active Clients (Custom)',
      kyc: 'KYC Verified (Custom)'
    }
  };
  return labels[timeFilter] || labels.this_month;
};

const formatCurrency = (value, precise = false) => {
  if (!value && value !== 0) return '₹0';
  if (precise) {
    // Show exact figure with full decimal precision
    return `₹${Number(value).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
  }
  if (value >= 10000000) return `₹${(value / 10000000).toFixed(2)} Cr`;
  if (value >= 100000) return `₹${(value / 100000).toFixed(2)} L`;
  return `₹${Number(value).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};

// Exact currency format for revenue (no rounding to Cr/L)
const formatExactCurrency = (value) => {
  if (!value && value !== 0) return '₹0';
  return `₹${Number(value).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};

const StatCard = ({ title, value, icon: Icon, trend, trendValue, color = 'gold' }) => {
  const colorClasses = {
    gold: 'text-[#C5A059]',
    green: 'text-emerald-500',
    blue: 'text-sky-500',
    red: 'text-red-500'
  };
  
  return (
    <Card className="card-vault stat-card" data-testid={`stat-card-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="label-uppercase mb-2">{title}</p>
            <p className={`font-heading text-2xl lg:text-3xl font-bold ${colorClasses[color]} tabular-nums`}>
              {value}
            </p>
            {trend && (
              <div className={`flex items-center mt-2 text-sm ${trend === 'up' ? 'text-emerald-500' : 'text-red-500'}`}>
                {trend === 'up' ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                <span className="ml-1">{trendValue}</span>
              </div>
            )}
          </div>
          <div className={`p-3 rounded-lg bg-white/5 ${colorClasses[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="glass p-3 rounded-lg border border-white/10">
        <p className="font-heading text-white text-sm mb-1">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="font-mono text-sm" style={{ color: entry.color }}>
            {entry.name}: {typeof entry.value === 'number' && entry.value > 1000 
              ? formatCurrency(entry.value) 
              : entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const COLORS = ['#C5A059', '#0EA5E9', '#10B981', '#EF4444', '#8B5CF6'];

// Ledger Balance Widget Component
const LedgerBalanceWidget = ({ token }) => {
  const [ledgerData, setLedgerData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortDirection, setSortDirection] = useState('desc'); // desc = highest first
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchLedgerBalances();
  }, [token]);

  const fetchLedgerBalances = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API_URL}/ledger-balances`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLedgerData(response.data || []);
    } catch (error) {
      console.error('Failed to fetch ledger balances:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter by search query
  const filteredData = ledgerData.filter(item => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      (item.client_code || '').toLowerCase().includes(query) ||
      (item.client_name || '').toLowerCase().includes(query) ||
      (item.phone || '').includes(query)
    );
  });

  // Sort data based on current direction (no limit - show all, use scroll)
  const sortedData = [...filteredData].sort((a, b) => {
    const aVal = a.ledger_balance || 0;
    const bVal = b.ledger_balance || 0;
    return sortDirection === 'desc' ? bVal - aVal : aVal - bVal;
  }); // Show all records (scrollable)

  const toggleSort = () => {
    setSortDirection(prev => prev === 'desc' ? 'asc' : 'desc');
  };

  // Calculate summary stats (from filtered data)
  const totalPositive = filteredData.filter(d => d.ledger_balance > 0).reduce((sum, d) => sum + d.ledger_balance, 0);
  const totalNegative = filteredData.filter(d => d.ledger_balance < 0).reduce((sum, d) => sum + d.ledger_balance, 0);
  const clientsWithBalance = filteredData.length;

  if (loading) {
    return (
      <Card className="card-vault">
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin w-6 h-6 border-2 border-[#C5A059] border-t-transparent rounded-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="card-vault" data-testid="ledger-balance-widget">
      <CardHeader className="border-b border-white/5">
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading text-white flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-[#C5A059]" />
            Latest Ledger Balances (Snapshot)
          </CardTitle>
          {ledgerData.length > 0 && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search by name, code, or phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-slate-800/50 border border-white/10 rounded-lg pl-9 pr-3 py-1.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#C5A059]/50 w-64"
                data-testid="ledger-search"
              />
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-6">
        {ledgerData.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p className="font-medium">No Ledger Data Available</p>
            <p className="text-sm mt-1">Import a ledger balance file to see the snapshot here.</p>
          </div>
        ) : (
          <>
            {/* Summary Row */}
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="bg-slate-800/50 rounded-lg p-3">
                <p className="text-xs text-slate-400 mb-1">Total Credit Balance</p>
                <p className="text-lg font-bold text-emerald-400">
                  {formatCurrency(totalPositive)}
                </p>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-3">
                <p className="text-xs text-slate-400 mb-1">Total Debit Balance</p>
                <p className="text-lg font-bold text-red-400">
                  {formatCurrency(Math.abs(totalNegative))}
                </p>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-3">
                <p className="text-xs text-slate-400 mb-1">Clients with Balance</p>
                <p className="text-lg font-bold text-white">{clientsWithBalance}</p>
              </div>
            </div>

            {/* Table - Scrollable */}
            <div className="border border-white/10 rounded-lg overflow-hidden">
              <div className="max-h-96 overflow-y-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-slate-900 z-10">
                    <TableRow className="border-b border-white/5 hover:bg-transparent">
                      <TableHead className="text-slate-400">Client Code</TableHead>
                      <TableHead className="text-slate-400">Client Name</TableHead>
                      <TableHead className="text-slate-400">Contact</TableHead>
                      <TableHead 
                        className="text-slate-400 text-right cursor-pointer hover:text-[#C5A059] transition-colors select-none"
                        onClick={toggleSort}
                      >
                        <div className="flex items-center justify-end gap-1">
                          Ledger Balance
                          <span className="text-[#C5A059]">
                            {sortDirection === 'desc' ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />}
                          </span>
                        </div>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                <TableBody>
                  {sortedData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-6 text-slate-400">
                        No matching records found
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedData.map((item, idx) => (
                      <TableRow key={idx} className="border-b border-white/5 hover:bg-white/5">
                        <TableCell>
                          <code className="text-[#C5A059] font-mono text-sm bg-[#C5A059]/10 px-2 py-1 rounded">
                            {item.client_code}
                          </code>
                        </TableCell>
                        <TableCell className="text-white">{item.client_name || '-'}</TableCell>
                        <TableCell className="text-slate-300 text-sm">
                          {item.phone ? (
                            <ClickToCall
                              phoneNumber={item.phone}
                              clientCode={item.client_code}
                              clientName={item.client_name}
                              token={token}
                            />
                          ) : '-'}
                        </TableCell>
                        <TableCell className={`text-right font-medium ${
                          item.ledger_balance > 0 
                            ? 'text-emerald-400' 
                            : item.ledger_balance < 0 
                              ? 'text-red-400' 
                              : 'text-slate-400'
                        }`}>
                          {formatCurrency(item.ledger_balance || 0)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              </div>
            </div>
            
            {filteredData.length > 0 && (
              <p className="text-xs text-slate-500 text-center mt-3">
                Showing {sortedData.length} clients • {sortDirection === 'desc' ? 'Highest' : 'Lowest'} balances first • Scroll to see all
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

// Pending Activations Widget Component (Dormant 3 - Awaiting Brokerage)
const PendingActivationsWidget = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/incentives/my-incentives?period=all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (err) {
      console.error('Failed to fetch activation data:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return null;

  const pending = data?.pending_activations || [];
  const completed = data?.activated_clients || [];
  
  if (pending.length === 0 && completed.length === 0) return null;

  return (
    <Card className="card-vault" data-testid="pending-activations-widget">
      <CardHeader className="border-b border-white/5">
        <CardTitle className="font-heading text-white flex items-center gap-2">
          <Clock className="w-5 h-5 text-yellow-500" />
          Dormant Activations
          {pending.length > 0 && (
            <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
              {pending.length} Awaiting
            </span>
          )}
          {completed.length > 0 && (
            <span className="ml-1 px-2 py-0.5 rounded-full text-xs bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
              {completed.length} Completed
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4">
        {pending.length > 0 && (
          <div className="mb-4">
            <p className="text-xs text-slate-400 mb-2 uppercase tracking-wider">Awaiting Brokerage</p>
            <div className="space-y-2">
              {pending.map((p, idx) => {
                const deadline = new Date(p.deadline_date);
                const today = new Date();
                const daysLeft = Math.ceil((deadline - today) / (1000 * 60 * 60 * 24));
                return (
                  <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-yellow-500/5 border border-yellow-500/15">
                    <div>
                      <p className="text-sm text-white font-medium">
                        {p.client_name || p.client_code}
                        {p.client_name && <span className="text-slate-500 ml-1 text-xs">({p.client_code})</span>}
                      </p>
                      <p className="text-xs text-slate-400">
                        Meeting: {new Date(p.meeting_date).toLocaleDateString()}
                      </p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      daysLeft <= 7 ? 'bg-red-500/20 text-red-400 border border-red-500/30' : 
                      daysLeft <= 14 ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' : 
                      'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                    }`}>
                      {daysLeft} days left
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {completed.length > 0 && (
          <div>
            <p className="text-xs text-slate-400 mb-2 uppercase tracking-wider">Recently Activated</p>
            <div className="space-y-2">
              {completed.slice(0, 5).map((a, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/15">
                  <div>
                    <p className="text-sm text-white font-medium">
                      {a.client_name || a.client_code}
                      {a.client_name && <span className="text-slate-500 ml-1 text-xs">({a.client_code})</span>}
                    </p>
                    <p className="text-xs text-slate-400">
                      Activated: {new Date(a.activation_date).toLocaleDateString()}
                    </p>
                  </div>
                  <span className="text-emerald-400 font-bold text-sm">
                    +{new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(a.incentive_amount || 0)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Dormant Clients Widget Component
const DormantClientsWidget = ({ token }) => {
  const [dormantSummary, setDormantSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState(null);
  const [dormantClients, setDormantClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [chartsData, setChartsData] = useState(null);
  const [chartsLoading, setChartsLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'dormant_days', direction: 'desc' });
  
  // Holdings/Portfolio state for AUM drill-down
  const [portfolioDialogOpen, setPortfolioDialogOpen] = useState(false);
  const [portfolioData, setPortfolioData] = useState(null);
  const [portfolioLoading, setPortfolioLoading] = useState(false);

  useEffect(() => {
    fetchDormantSummary();
  }, [token]);

  const fetchDormantSummary = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API_URL}/dashboard/dormant-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDormantSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch dormant summary:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDormantClients = async (status) => {
    setClientsLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/dashboard/dormant-clients/${status}?sort_by=${sortConfig.key}&sort_order=${sortConfig.direction}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDormantClients(response.data.clients || []);
    } catch (error) {
      console.error('Failed to fetch dormant clients:', error);
    } finally {
      setClientsLoading(false);
    }
  };

  // Fetch client portfolio/holdings for AUM drill-down
  const fetchClientPortfolio = async (clientCode) => {
    setPortfolioLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/clients/${clientCode}/portfolio`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPortfolioData(response.data);
      setPortfolioDialogOpen(true);
    } catch (error) {
      console.error('Failed to fetch portfolio:', error);
    } finally {
      setPortfolioLoading(false);
    }
  };

  const handleTabClick = (status) => {
    setSelectedTab(status);
    fetchDormantClients(status);
    fetchChartsData(status);
  };

  const fetchChartsData = async (status) => {
    setChartsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/dashboard/dormant-clients/${status}/charts?days=180`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChartsData(res.data);
    } catch (e) {
      console.error('Failed to fetch dormant charts', e);
      setChartsData(null);
    } finally {
      setChartsLoading(false);
    }
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  // Re-fetch when sort changes
  useEffect(() => {
    if (selectedTab) {
      fetchDormantClients(selectedTab);
    }
  }, [sortConfig]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return '-';
    }
  };

  if (loading) {
    return (
      <Card className="card-vault">
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin w-6 h-6 border-2 border-[#C5A059] border-t-transparent rounded-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!dormantSummary) return null;

  // Premium color palette for Gilded Vault theme
  // Active: Emerald Green - healthy, no action needed
  // Dormant 1: Soft Teal - manageable, less urgent
  // Dormant 2: Warm Amber/Gold - moderate attention needed  
  // Dormant 3: Deep Burgundy - requires action
  const tabs = [
    { 
      key: 'active', 
      label: 'Active', 
      color: '#10B981',  // Emerald green
      bgColor: 'rgba(16, 185, 129, 0.1)',
      hoverBg: 'rgba(16, 185, 129, 0.15)',
      textColor: '#10B981',
      borderColor: 'rgba(16, 185, 129, 0.3)',
      hoverBorder: 'rgba(16, 185, 129, 0.6)',
      subtitle: '0-12 days'
    },
    { 
      key: 'dormant_1', 
      label: 'Dormant 1', 
      color: '#5EEAD4',  // Soft teal
      bgColor: 'rgba(94, 234, 212, 0.1)',
      hoverBg: 'rgba(94, 234, 212, 0.15)',
      textColor: '#5EEAD4',
      borderColor: 'rgba(94, 234, 212, 0.3)',
      hoverBorder: 'rgba(94, 234, 212, 0.6)',
      subtitle: '13-25 days'
    },
    { 
      key: 'dormant_2', 
      label: 'Dormant 2', 
      color: '#C5A059',  // Gilded gold/amber
      bgColor: 'rgba(197, 160, 89, 0.1)',
      hoverBg: 'rgba(197, 160, 89, 0.15)',
      textColor: '#C5A059',
      borderColor: 'rgba(197, 160, 89, 0.3)',
      hoverBorder: 'rgba(197, 160, 89, 0.6)',
      subtitle: '26-59 days'
    },
    { 
      key: 'dormant_3', 
      label: 'Dormant 3', 
      color: '#E07A7A',  // Soft burgundy/rose
      bgColor: 'rgba(224, 122, 122, 0.1)',
      hoverBg: 'rgba(224, 122, 122, 0.15)',
      textColor: '#E07A7A',
      borderColor: 'rgba(224, 122, 122, 0.3)',
      hoverBorder: 'rgba(224, 122, 122, 0.6)',
      subtitle: '60+ days'
    }
  ];

  return (
    <>
      <Card className="card-vault" data-testid="dormant-clients-widget">
        <CardHeader className="border-b border-white/5">
          <CardTitle className="font-heading text-white flex items-center gap-2">
            <Clock className="w-5 h-5 text-[#C5A059]" />
            Dormant Clients Overview
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-4 gap-4">
            {tabs.map(tab => {
              const data = dormantSummary[tab.key];
              return (
                <button
                  key={tab.key}
                  onClick={() => handleTabClick(tab.key)}
                  data-testid={`dormant-tile-${tab.key}`}
                  className="p-5 rounded-xl transition-all duration-300 hover:scale-[1.02] cursor-pointer group"
                  style={{
                    background: `linear-gradient(135deg, ${tab.bgColor} 0%, rgba(30, 41, 59, 0.5) 100%)`,
                    border: `1px solid ${tab.borderColor}`,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = tab.hoverBorder;
                    e.currentTarget.style.boxShadow = `0 8px 32px ${tab.bgColor}`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = tab.borderColor;
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span 
                      className="text-sm font-semibold tracking-wide uppercase"
                      style={{ color: tab.textColor }}
                    >
                      {tab.label}
                    </span>
                    <span 
                      className="w-2.5 h-2.5 rounded-full ring-2 ring-offset-1 ring-offset-slate-900"
                      style={{ backgroundColor: tab.color, ringColor: tab.color }}
                    />
                  </div>
                  <div className="text-3xl font-bold text-white mb-1">{data?.count || 0}</div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-400">
                      {data?.percentage || 0}% of clients
                    </span>
                    <span 
                      className="text-xs font-medium px-2 py-0.5 rounded-full"
                      style={{ 
                        backgroundColor: tab.bgColor,
                        color: tab.textColor
                      }}
                    >
                      {tab.subtitle}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Dormant Clients Detail Dialog */}
      <Dialog open={selectedTab !== null} onOpenChange={() => { setSelectedTab(null); setChartsData(null); }}>
        <DialogContent className="max-w-[95vw] w-[1400px] max-h-[85vh] overflow-hidden flex flex-col bg-slate-900 border-white/10">
          <DialogHeader className="border-b border-white/5 pb-4">
            <DialogTitle className="text-white flex items-center gap-3">
              <Clock className="w-5 h-5 text-[#C5A059]" />
              {selectedTab === 'active' && (
                <span className="flex items-center gap-2">
                  <span style={{ color: '#10B981' }}>Active Clients</span>
                  <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(16, 185, 129, 0.15)', color: '#10B981' }}>0-12 days</span>
                </span>
              )}
              {selectedTab === 'dormant_1' && (
                <span className="flex items-center gap-2">
                  <span style={{ color: '#5EEAD4' }}>Dormant 1</span>
                  <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(94, 234, 212, 0.15)', color: '#5EEAD4' }}>13-25 days</span>
                </span>
              )}
              {selectedTab === 'dormant_2' && (
                <span className="flex items-center gap-2">
                  <span style={{ color: '#C5A059' }}>Dormant 2</span>
                  <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(197, 160, 89, 0.15)', color: '#C5A059' }}>26-59 days</span>
                </span>
              )}
              {selectedTab === 'dormant_3' && (
                <span className="flex items-center gap-2">
                  <span style={{ color: '#E07A7A' }}>Dormant 3</span>
                  <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(224, 122, 122, 0.15)', color: '#E07A7A' }}>60+ days</span>
                </span>
              )}
              <span className="text-slate-400 ml-2">• {dormantClients.length} Clients</span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden flex gap-4">
            {/* TABLE 60% */}
            <div className="w-3/5 overflow-auto pr-2">
            {clientsLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin w-8 h-8 border-2 border-[#C5A059] border-t-transparent rounded-full" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-white/5 hover:bg-transparent">
                    <TableHead 
                      className="text-slate-400 cursor-pointer hover:text-[#C5A059]"
                      onClick={() => handleSort('client_code')}
                    >
                      <div className="flex items-center gap-1">
                        Client Code
                        {sortConfig.key === 'client_code' && (
                          <span className="text-[#C5A059]">{sortConfig.direction === 'desc' ? '↓' : '↑'}</span>
                        )}
                      </div>
                    </TableHead>
                    <TableHead className="text-slate-400">Client Name</TableHead>
                    <TableHead className="text-slate-400">Contact</TableHead>
                    <TableHead 
                      className="text-slate-400 cursor-pointer hover:text-[#C5A059]"
                      onClick={() => handleSort('assigned_dealer_id')}
                    >
                      <div className="flex items-center gap-1">
                        Dealer
                        {sortConfig.key === 'assigned_dealer_id' && (
                          <span className="text-[#C5A059]">{sortConfig.direction === 'desc' ? '↓' : '↑'}</span>
                        )}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="text-slate-400 cursor-pointer hover:text-[#C5A059]"
                      onClick={() => handleSort('assigned_rm_id')}
                    >
                      <div className="flex items-center gap-1">
                        RM
                        {sortConfig.key === 'assigned_rm_id' && (
                          <span className="text-[#C5A059]">{sortConfig.direction === 'desc' ? '↓' : '↑'}</span>
                        )}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="text-slate-400 text-right cursor-pointer hover:text-[#C5A059]"
                      onClick={() => handleSort('aum')}
                    >
                      <div className="flex items-center justify-end gap-1">
                        AUM
                        {sortConfig.key === 'aum' && (
                          <span className="text-[#C5A059]">{sortConfig.direction === 'desc' ? '↓' : '↑'}</span>
                        )}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="text-slate-400 text-right cursor-pointer hover:text-[#C5A059]"
                      onClick={() => handleSort('total_brokerage')}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Brokerage
                        {sortConfig.key === 'total_brokerage' && (
                          <span className="text-[#C5A059]">{sortConfig.direction === 'desc' ? '↓' : '↑'}</span>
                        )}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="text-slate-400 text-right cursor-pointer hover:text-[#C5A059]"
                      onClick={() => handleSort('ledger_balance')}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Ledger Balance
                        {sortConfig.key === 'ledger_balance' && (
                          <span className="text-[#C5A059]">{sortConfig.direction === 'desc' ? '↓' : '↑'}</span>
                        )}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="text-slate-400 cursor-pointer hover:text-[#C5A059]"
                      onClick={() => handleSort('last_brokerage_date')}
                    >
                      <div className="flex items-center gap-1">
                        Last Brokerage
                        {sortConfig.key === 'last_brokerage_date' && (
                          <span className="text-[#C5A059]">{sortConfig.direction === 'desc' ? '↓' : '↑'}</span>
                        )}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="text-slate-400 text-right cursor-pointer hover:text-[#C5A059]"
                      onClick={() => handleSort('dormant_days')}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Dormant Days
                        {sortConfig.key === 'dormant_days' && (
                          <span className="text-[#C5A059]">{sortConfig.direction === 'desc' ? '↓' : '↑'}</span>
                        )}
                      </div>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dormantClients.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center py-8 text-slate-500">
                        No clients found in this category
                      </TableCell>
                    </TableRow>
                  ) : (
                    dormantClients.map((client, idx) => (
                      <TableRow key={idx} className="border-b border-white/5 hover:bg-white/5">
                        <TableCell>
                          <code className="text-[#C5A059] font-mono text-sm bg-[#C5A059]/10 px-2 py-1 rounded">
                            {client.client_code}
                          </code>
                        </TableCell>
                        <TableCell className="text-white">{client.name}</TableCell>
                        <TableCell className="text-slate-300 text-sm">
                          {client.phone ? (
                            <ClickToCall
                              phoneNumber={client.phone}
                              clientCode={client.client_code}
                              clientName={client.name}
                              token={token}
                            />
                          ) : '-'}
                        </TableCell>
                        <TableCell className="text-slate-300 text-sm">{client.assigned_dealer_id || '-'}</TableCell>
                        <TableCell className="text-slate-300 text-sm">{client.assigned_rm_id || '-'}</TableCell>
                        <TableCell className="text-right">
                          <button
                            onClick={() => fetchClientPortfolio(client.client_code)}
                            className="text-white font-medium hover:text-[#C5A059] cursor-pointer underline decoration-dotted underline-offset-2 transition-colors"
                            data-testid={`dormant-aum-${client.client_code}`}
                          >
                            {formatCurrency(client.aum || 0)}
                          </button>
                        </TableCell>
                        <TableCell className="text-right text-emerald-400">{formatCurrency(client.total_brokerage || 0)}</TableCell>
                        <TableCell className={`text-right ${
                          client.ledger_balance > 0 ? 'text-emerald-400' : 
                          client.ledger_balance < 0 ? 'text-red-400' : 'text-slate-400'
                        }`}>
                          {client.ledger_balance ? formatCurrency(client.ledger_balance) : '-'}
                        </TableCell>
                        <TableCell className="text-slate-300">{formatDate(client.last_brokerage_date)}</TableCell>
                        <TableCell className="text-right">
                          <span className={`font-medium ${
                            client.dormant_days > 60 ? 'text-red-400' :
                            client.dormant_days > 26 ? 'text-yellow-400' :
                            'text-emerald-400'
                          }`}>
                            {client.dormant_days ?? '-'}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
            </div>
            
            {/* CHARTS 40% */}
            <div className="w-2/5 overflow-auto pl-2 border-l border-white/5 space-y-3" data-testid="dormant-charts-column">
              {chartsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin w-6 h-6 border-2 border-[#C5A059] border-t-transparent rounded-full" />
                </div>
              ) : chartsData ? (
                <DormantChartsPanel data={chartsData} status={selectedTab} />
              ) : (
                <div className="text-center text-slate-400 py-8">No chart data</div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Holdings Detail Dialog - AUM Drill-down */}
      <Dialog open={portfolioDialogOpen} onOpenChange={setPortfolioDialogOpen}>
        <DialogContent className="bg-[#0F172A] border-white/10 max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading text-white flex items-center gap-2">
              <Wallet className="w-5 h-5 text-[#C5A059]" />
              Holdings Detail
              {portfolioData && (
                <span className="ml-2 text-sm text-slate-400">
                  {portfolioData.client_code} - {portfolioData.client_name}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {portfolioLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-8 h-8 border-2 border-[#C5A059] border-t-transparent rounded-full" />
            </div>
          ) : portfolioData ? (
            <div className="flex-1 overflow-auto space-y-4 mt-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-slate-800/50 border border-white/10 rounded-lg p-4">
                  <p className="text-slate-400 text-sm">Total AUM</p>
                  <p className="text-2xl font-bold text-white">{formatCurrency(portfolioData.total_aum)}</p>
                </div>
                <div className="bg-slate-800/50 border border-white/10 rounded-lg p-4">
                  <p className="text-slate-400 text-sm">Notional P&L</p>
                  <p className={`text-2xl font-bold ${portfolioData.total_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {portfolioData.total_pnl >= 0 ? '+' : ''}{formatCurrency(portfolioData.total_pnl)}
                  </p>
                </div>
                <div className="bg-slate-800/50 border border-white/10 rounded-lg p-4">
                  <p className="text-slate-400 text-sm">Holdings Count</p>
                  <p className="text-2xl font-bold text-white">{portfolioData.holdings_count}</p>
                </div>
              </div>

              {/* Holdings Table */}
              <div className="overflow-x-auto max-h-[400px] overflow-y-auto border border-white/10 rounded-lg">
                <Table>
                  <TableHeader className="sticky top-0 bg-[#0F172A]">
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="text-slate-400">ISIN / Security</TableHead>
                      <TableHead className="text-slate-400 text-right">Quantity</TableHead>
                      <TableHead className="text-slate-400 text-right">Close Price</TableHead>
                      <TableHead className="text-slate-400 text-right">Market Value</TableHead>
                      <TableHead className="text-slate-400 text-right">Buy Avg</TableHead>
                      <TableHead className="text-slate-400 text-right">Notional P&L</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {portfolioData.holdings?.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-slate-400">
                          No holdings found
                        </TableCell>
                      </TableRow>
                    ) : (
                      portfolioData.holdings?.map((holding, idx) => (
                        <TableRow key={holding.isin || idx} className="border-white/5 hover:bg-white/5">
                          <TableCell>
                            <div>
                              <p className="text-white font-medium">{holding.security_name || holding.scrip_name || '-'}</p>
                              <p className="text-slate-500 text-xs font-mono">{holding.isin || '-'}</p>
                            </div>
                          </TableCell>
                          <TableCell className="text-right text-white">
                            {(holding.quantity || holding.qty || 0).toLocaleString('en-IN')}
                          </TableCell>
                          <TableCell className="text-right text-slate-300">
                            ₹{(holding.close_price || holding.ltp || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </TableCell>
                          <TableCell className="text-right text-white font-medium">
                            {formatCurrency(holding.holding_value || holding.market_value || 0)}
                          </TableCell>
                          <TableCell className="text-right text-slate-300">
                            ₹{(holding.buy_avg || holding.avg_price || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </TableCell>
                          <TableCell className={`text-right font-medium ${(holding.notional_pnl || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {(holding.notional_pnl || 0) >= 0 ? '+' : ''}{formatCurrency(holding.notional_pnl || 0)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          ) : (
            <div className="text-center py-12 text-slate-400">
              No portfolio data available
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

// =============================================================================
// Active Clients Dialog - Shows list of clients with brokerage in selected period
// =============================================================================
const ActiveClientsDialog = ({ open, onClose, token, timeFilter = 'this_month', customRange = null }) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState('last_brokerage_date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [dailyTrend, setDailyTrend] = useState([]);
  const [tierDistribution, setTierDistribution] = useState([]);
  const [aumBuckets, setAumBuckets] = useState([]);
  const [breakdown, setBreakdown] = useState([]);
  const [breakdownLabel, setBreakdownLabel] = useState(null);
  
  // Holdings/Portfolio state for AUM drill-down
  const [portfolioDialogOpen, setPortfolioDialogOpen] = useState(false);
  const [portfolioData, setPortfolioData] = useState(null);
  const [portfolioLoading, setPortfolioLoading] = useState(false);

  useEffect(() => {
    if (open) {
      fetchActiveClients();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sortBy, sortOrder, timeFilter, customRange?.from, customRange?.to]);

  const fetchActiveClients = async () => {
    setLoading(true);
    try {
      const params = { sort_by: sortBy, sort_order: sortOrder, page_size: 500, time_filter: timeFilter };
      if (timeFilter === 'custom' && customRange?.from && customRange?.to) {
        params.date_from = new Date(customRange.from).toISOString().split('T')[0];
        params.date_to = new Date(customRange.to).toISOString().split('T')[0];
      }
      const response = await axios.get(`${API_URL}/dashboard/active-clients`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data.clients || []);
      setDateRange(response.data.date_range);
      setDailyTrend(response.data.daily_trend || []);
      setTierDistribution(response.data.tier_distribution || []);
      setAumBuckets(response.data.aum_buckets || []);
      setBreakdown(response.data.breakdown || []);
      setBreakdownLabel(response.data.breakdown_label || null);
    } catch (error) {
      console.error('Failed to fetch active clients:', error);
    } finally {
      setLoading(false);
    }
  };

  // Match stat card label (reuse same labels dictionary)
  const periodLabel = (() => {
    const widgetLabels = getWidgetLabels(timeFilter, customRange);
    // widgetLabels.active e.g. "Active Clients (This Month)" — use as-is
    return widgetLabels.active;
  })();

  // Fetch client portfolio/holdings for AUM drill-down
  const fetchClientPortfolio = async (clientCode) => {
    setPortfolioLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/clients/${clientCode}/portfolio`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPortfolioData(response.data);
      setPortfolioDialogOpen(true);
    } catch (error) {
      console.error('Failed to fetch portfolio:', error);
    } finally {
      setPortfolioLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return null;
    return sortOrder === 'asc' ? <ArrowUp className="w-3 h-3 ml-1" /> : <ArrowDown className="w-3 h-3 ml-1" />;
  };

  const filteredClients = clients.filter(c => 
    !searchTerm || 
    c.client_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.dealer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.rm_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] w-[1400px] max-h-[95vh] overflow-hidden flex flex-col bg-[#1a1a2e] border-[#C5A059]/20">
        <DialogHeader className="border-b border-white/5 pb-4">
          <DialogTitle className="text-xl text-white flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-500" />
            {periodLabel}
            <span className="ml-2 px-2 py-1 bg-emerald-500/20 text-emerald-400 text-sm rounded-full">
              {filteredClients.length} clients
            </span>
            {dateRange && (
              <span className="text-sm text-slate-400 ml-2 font-normal">
                • {dateRange.from} to {dateRange.to}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex gap-4 pt-4">
          {/* TABLE 60% */}
          <div className="w-3/5 overflow-auto pr-2 flex flex-col">
            {/* Search */}
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search by client code, name, RM, or dealer..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-[#C5A059]/50 text-sm"
                data-testid="active-clients-search"
              />
            </div>
            {/* Table */}
            <div className="flex-1 overflow-auto">
              {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-8 h-8 border-2 border-[#C5A059] border-t-transparent rounded-full" />
            </div>
          ) : filteredClients.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              No active clients found in the selected period
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700 hover:bg-transparent">
                  <TableHead 
                    className="text-[#C5A059] cursor-pointer hover:text-white"
                    onClick={() => handleSort('client_code')}
                  >
                    <div className="flex items-center">Client Code <SortIcon field="client_code" /></div>
                  </TableHead>
                  <TableHead 
                    className="text-[#C5A059] cursor-pointer hover:text-white"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center">Client Name <SortIcon field="name" /></div>
                  </TableHead>
                  <TableHead className="text-[#C5A059]">Contact</TableHead>
                  <TableHead 
                    className="text-[#C5A059] cursor-pointer hover:text-white"
                    onClick={() => handleSort('rm_name')}
                  >
                    <div className="flex items-center">RM <SortIcon field="rm_name" /></div>
                  </TableHead>
                  <TableHead 
                    className="text-[#C5A059] cursor-pointer hover:text-white"
                    onClick={() => handleSort('dealer_name')}
                  >
                    <div className="flex items-center">Dealer <SortIcon field="dealer_name" /></div>
                  </TableHead>
                  <TableHead 
                    className="text-[#C5A059] text-right cursor-pointer hover:text-white"
                    onClick={() => handleSort('total_aum')}
                  >
                    <div className="flex items-center justify-end">AUM <SortIcon field="total_aum" /></div>
                  </TableHead>
                  <TableHead 
                    className="text-[#C5A059] text-right cursor-pointer hover:text-white"
                    onClick={() => handleSort('brokerage_ytd')}
                  >
                    <div className="flex items-center justify-end">Brokerage (YTD) <SortIcon field="brokerage_ytd" /></div>
                  </TableHead>
                  <TableHead 
                    className="text-[#C5A059] text-right cursor-pointer hover:text-white"
                    onClick={() => handleSort('last_brokerage_date')}
                  >
                    <div className="flex items-center justify-end">Last Brokerage <SortIcon field="last_brokerage_date" /></div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredClients.map((client) => (
                  <TableRow key={client.id || client.client_code} className="border-slate-700/50 hover:bg-slate-800/30">
                    <TableCell className="font-medium text-white">{client.client_code}</TableCell>
                    <TableCell className="text-slate-300">{client.name}</TableCell>
                    <TableCell className="text-slate-400">
                      {client.phone ? (
                        <ClickToCall
                          phoneNumber={client.phone}
                          clientCode={client.client_code}
                          clientName={client.name}
                          token={token}
                        />
                      ) : '-'}
                    </TableCell>
                    <TableCell className="text-slate-400">{client.rm_name || client.rm_code || '-'}</TableCell>
                    <TableCell className="text-slate-400">{client.dealer_name || client.dealer_code || '-'}</TableCell>
                    <TableCell className="text-right">
                      <button
                        onClick={() => fetchClientPortfolio(client.client_code)}
                        className="text-emerald-400 font-medium hover:text-[#C5A059] hover:bg-[#C5A059]/10 cursor-pointer underline underline-offset-2 transition-all px-2 py-1 rounded"
                        data-testid={`active-aum-${client.client_code}`}
                        title="Click to view holdings"
                      >
                        {formatCurrency(client.total_aum || 0)}
                      </button>
                    </TableCell>
                    <TableCell className="text-right text-sky-400 font-medium">
                      {formatCurrency(client.brokerage_ytd || 0)}
                    </TableCell>
                    <TableCell className="text-right text-slate-300">
                      {client.last_brokerage_date || '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
            </div>
          </div>
          {/* CHARTS 40% */}
          <div className="w-2/5 overflow-auto pl-2 border-l border-white/5 space-y-3" data-testid="active-clients-charts-column">
            <PeriodChartsPanel
              dailyTrend={dailyTrend}
              tierDistribution={tierDistribution}
              aumBuckets={aumBuckets}
              breakdown={breakdown}
              breakdownLabel={breakdownLabel}
              metric="count"
              primaryColor="#10B981"
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Holdings Detail Dialog - AUM Drill-down */}
    <Dialog open={portfolioDialogOpen} onOpenChange={setPortfolioDialogOpen}>
      <DialogContent className="bg-[#0F172A] border-white/10 max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-heading text-white flex items-center gap-2">
            <Wallet className="w-5 h-5 text-[#C5A059]" />
            Holdings Detail
            {portfolioData && (
              <span className="ml-2 text-sm text-slate-400">
                {portfolioData.client_code} - {portfolioData.client_name}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>
        
        {portfolioLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-[#C5A059] border-t-transparent rounded-full" />
          </div>
        ) : portfolioData ? (
          <div className="flex-1 overflow-auto space-y-4 mt-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-slate-800/50 border border-white/10 rounded-lg p-4">
                <p className="text-slate-400 text-sm">Total AUM</p>
                <p className="text-2xl font-bold text-white">{formatCurrency(portfolioData.total_aum)}</p>
              </div>
              <div className="bg-slate-800/50 border border-white/10 rounded-lg p-4">
                <p className="text-slate-400 text-sm">Notional P&L</p>
                <p className={`text-2xl font-bold ${portfolioData.total_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {portfolioData.total_pnl >= 0 ? '+' : ''}{formatCurrency(portfolioData.total_pnl)}
                </p>
              </div>
              <div className="bg-slate-800/50 border border-white/10 rounded-lg p-4">
                <p className="text-slate-400 text-sm">Holdings Count</p>
                <p className="text-2xl font-bold text-white">{portfolioData.holdings_count}</p>
              </div>
            </div>

            {/* Holdings Table */}
            <div className="overflow-x-auto max-h-[400px] overflow-y-auto border border-white/10 rounded-lg">
              <Table>
                <TableHeader className="sticky top-0 bg-[#0F172A]">
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-slate-400">ISIN / Security</TableHead>
                    <TableHead className="text-slate-400 text-right">Quantity</TableHead>
                    <TableHead className="text-slate-400 text-right">Close Price</TableHead>
                    <TableHead className="text-slate-400 text-right">Market Value</TableHead>
                    <TableHead className="text-slate-400 text-right">Buy Avg</TableHead>
                    <TableHead className="text-slate-400 text-right">Notional P&L</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {portfolioData.holdings?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-slate-400">
                        No holdings found
                      </TableCell>
                    </TableRow>
                  ) : (
                    portfolioData.holdings?.map((holding, idx) => (
                      <TableRow key={holding.isin || idx} className="border-white/5 hover:bg-white/5">
                        <TableCell>
                          <div>
                            <p className="text-white font-medium">{holding.security_name || holding.scrip_name || '-'}</p>
                            <p className="text-slate-500 text-xs font-mono">{holding.isin || '-'}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-right text-white">
                          {(holding.quantity || holding.qty || 0).toLocaleString('en-IN')}
                        </TableCell>
                        <TableCell className="text-right text-slate-300">
                          ₹{(holding.close_price || holding.ltp || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell className="text-right text-white font-medium">
                          {formatCurrency(holding.holding_value || holding.market_value || 0)}
                        </TableCell>
                        <TableCell className="text-right text-slate-300">
                          ₹{(holding.buy_avg || holding.avg_price || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell className={`text-right font-medium ${(holding.notional_pnl || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {(holding.notional_pnl || 0) >= 0 ? '+' : ''}{formatCurrency(holding.notional_pnl || 0)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        ) : (
          <div className="text-center py-12 text-slate-400">
            No portfolio data available
          </div>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
};

// =============================================================================
// Dormant Charts Panel (Tier donut + AUM bucket bar + By Dealer/RM bar + Movement line)
// =============================================================================
const DormantChartsPanel = ({ data, status }) => {
  const [movementMode, setMovementMode] = useState('single'); // 'single' | 'all'
  if (!data) return null;
  const tier = data.tier_distribution || [];
  const aum = data.aum_buckets || [];
  const breakdown = data.breakdown || [];
  const movement = data.movement_trend || [];
  
  const statusKey = status;
  const statusLabel = {
    active: 'Active',
    dormant_1: 'Dormant 1',
    dormant_2: 'Dormant 2',
    dormant_3: 'Dormant 3'
  }[statusKey] || statusKey;
  const statusColor = {
    active: '#10B981',
    dormant_1: '#F59E0B',
    dormant_2: '#F97316',
    dormant_3: '#EF4444'
  }[statusKey] || '#C5A059';

  const totalTier = tier.reduce((s, t) => s + t.count, 0) || 1;

  return (
    <>
      {/* By Tier - Donut */}
      <Card className="bg-slate-800/50 border-white/10">
        <CardContent className="p-3">
          <p className="text-xs text-slate-400 mb-2">By Tier</p>
          {tier.length === 0 ? (
            <p className="text-center text-slate-500 py-4 text-xs">No tier data</p>
          ) : (
            <div className="flex items-center gap-3" data-testid="dormant-tier-chart">
              <div className="w-32 h-32 flex-shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={tier} dataKey="count" nameKey="label" cx="50%" cy="50%" outerRadius={60} innerRadius={30}>
                      {tier.map((e, i) => <Cell key={i} fill={e.color} />)}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', fontSize: 12 }} formatter={(v, n) => [v, n]} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex-1 space-y-1">
                {tier.map(t => (
                  <div key={t.tier} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: t.color }} />
                      <span className="text-slate-300">{t.label}</span>
                    </div>
                    <span className="text-white font-medium">{t.count} <span className="text-slate-500">({((t.count / totalTier) * 100).toFixed(0)}%)</span></span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* AUM Bucket - Bar */}
      <Card className="bg-slate-800/50 border-white/10">
        <CardContent className="p-3">
          <p className="text-xs text-slate-400 mb-2">By AUM Bucket</p>
          <div className="h-[140px]" data-testid="dormant-aum-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={aum} margin={{ top: 5, right: 5, bottom: 5, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="label" stroke="#94a3b8" tick={{ fontSize: 10 }} />
                <YAxis stroke="#94a3b8" tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', fontSize: 12 }} formatter={(v) => [v, 'Clients']} />
                <Bar dataKey="count" fill={statusColor} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* By Dealer / By RM - Bar (if applicable) */}
      {breakdown.length > 0 && (
        <Card className="bg-slate-800/50 border-white/10">
          <CardContent className="p-3">
            <p className="text-xs text-slate-400 mb-2">{data.breakdown_label}</p>
            <div className="h-[150px]" data-testid="dormant-breakdown-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={breakdown.slice(0, 10)} layout="vertical" margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis type="number" stroke="#94a3b8" tick={{ fontSize: 10 }} allowDecimals={false} />
                  <YAxis type="category" dataKey="label" stroke="#94a3b8" tick={{ fontSize: 10 }} width={80} />
                  <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', fontSize: 12 }} formatter={(v) => [v, 'Clients']} />
                  <Bar dataKey="count" fill="#C5A059" radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Movement Trend - Line (180d rolling) */}
      <Card className="bg-slate-800/50 border-white/10">
        <CardContent className="p-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs text-slate-400">
              {movementMode === 'single' ? `${statusLabel} - Movement (180d rolling)` : 'All Categories - Movement (180d rolling)'}
            </p>
            <div className="flex bg-slate-900/60 rounded-md p-0.5 border border-white/10">
              <button
                onClick={() => setMovementMode('single')}
                className={`text-[10px] px-2 py-0.5 rounded ${movementMode === 'single' ? 'bg-[#C5A059] text-slate-900 font-medium' : 'text-slate-400 hover:text-white'}`}
                data-testid="movement-mode-single"
              >
                This Category
              </button>
              <button
                onClick={() => setMovementMode('all')}
                className={`text-[10px] px-2 py-0.5 rounded ${movementMode === 'all' ? 'bg-[#C5A059] text-slate-900 font-medium' : 'text-slate-400 hover:text-white'}`}
                data-testid="movement-mode-all"
              >
                All 4
              </button>
            </div>
          </div>
          {movementMode === 'all' && (
            <div className="flex gap-3 mb-1 text-[10px]">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" />Active</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" />D1</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-500" />D2</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" />D3</span>
            </div>
          )}
          <div className="h-[150px]" data-testid="dormant-movement-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={movement} margin={{ top: 5, right: 5, bottom: 5, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis
                  dataKey="date"
                  stroke="#94a3b8"
                  tick={{ fontSize: 9 }}
                  interval={Math.floor((movement.length || 30) / 6)}
                  tickFormatter={(val) => new Date(val).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                />
                <YAxis stroke="#94a3b8" tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', fontSize: 12 }}
                  labelFormatter={(label) => new Date(label).toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}
                  formatter={(value, name) => {
                    const labels = { active: 'Active', dormant_1: 'Dormant 1', dormant_2: 'Dormant 2', dormant_3: 'Dormant 3' };
                    return [value, labels[name] || name];
                  }}
                />
                {movementMode === 'single' ? (
                  <Line type="monotone" dataKey={statusKey} stroke={statusColor} strokeWidth={2} dot={false} />
                ) : (
                  <>
                    <Line type="monotone" dataKey="active" stroke="#10B981" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="dormant_1" stroke="#F59E0B" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="dormant_2" stroke="#F97316" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="dormant_3" stroke="#EF4444" strokeWidth={2} dot={false} />
                  </>
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </>
  );
};

// =============================================================================
// Period Charts Panel - Reusable side panel for Active Clients & Revenue dialogs
// Renders: Daily Trend bar + Tier donut + AUM bucket bar + Breakdown bar (if any)
// metric: 'count' (Active Clients) or 'revenue' (Revenue)
// =============================================================================
const PeriodChartsPanel = ({
  dailyTrend = [],
  tierDistribution = [],
  aumBuckets = [],
  breakdown = [],
  breakdownLabel = null,
  metric = 'count', // 'count' | 'revenue'
  primaryColor = '#10B981'
}) => {
  const isRevenue = metric === 'revenue';
  const dataKey = isRevenue ? 'brokerage' : 'active_clients';
  const tierKey = isRevenue ? 'revenue' : 'count';
  const aumKey = isRevenue ? 'revenue' : 'count';
  const breakdownKey = isRevenue ? 'revenue' : 'count';

  const totalTier = tierDistribution.reduce((s, t) => s + (t[tierKey] || 0), 0) || 1;

  const fmtY = (v) => {
    if (!isRevenue) return v;
    return v >= 100000 ? `₹${(v / 100000).toFixed(0)}L` : `₹${(v / 1000).toFixed(0)}k`;
  };
  const fmtTip = (v) => {
    if (!isRevenue) return [v, 'Active Clients'];
    return [formatCurrency(v), 'Revenue'];
  };

  return (
    <>
      {/* Daily Trend - Bar */}
      <Card className="bg-slate-800/50 border-white/10">
        <CardContent className="p-3">
          <p className="text-xs text-slate-400 mb-2">
            {isRevenue ? 'Daily Revenue Trend' : 'Daily Active Clients Trend'}
          </p>
          {dailyTrend.length === 0 ? (
            <p className="text-center text-slate-500 py-4 text-xs">No trade data in selected period</p>
          ) : (
            <div className="h-[140px]" data-testid="period-daily-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dailyTrend} margin={{ top: 5, right: 5, bottom: 5, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis
                    dataKey="date"
                    stroke="#94a3b8"
                    tick={{ fontSize: 9 }}
                    interval={Math.floor((dailyTrend.length || 30) / 6)}
                    tickFormatter={(val) => new Date(val).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                  />
                  <YAxis stroke="#94a3b8" tick={{ fontSize: 9 }} allowDecimals={false} tickFormatter={fmtY} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', fontSize: 12 }}
                    labelFormatter={(label) => new Date(label).toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}
                    formatter={(value) => fmtTip(value)}
                  />
                  <Bar dataKey={dataKey} fill={primaryColor} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* By Tier - Donut */}
      <Card className="bg-slate-800/50 border-white/10">
        <CardContent className="p-3">
          <p className="text-xs text-slate-400 mb-2">{isRevenue ? 'Revenue by Tier' : 'By Tier'}</p>
          {tierDistribution.length === 0 ? (
            <p className="text-center text-slate-500 py-4 text-xs">No tier data</p>
          ) : (
            <div className="flex items-center gap-3" data-testid="period-tier-chart">
              <div className="w-32 h-32 flex-shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={tierDistribution} dataKey={tierKey} nameKey="label" cx="50%" cy="50%" outerRadius={60} innerRadius={30}>
                      {tierDistribution.map((e, i) => <Cell key={i} fill={e.color} />)}
                    </Pie>
                    <Tooltip
                      contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', fontSize: 12 }}
                      formatter={(v, n) => [isRevenue ? formatCurrency(v) : v, n]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex-1 space-y-1">
                {tierDistribution.map(t => {
                  const val = t[tierKey] || 0;
                  const pct = ((val / totalTier) * 100).toFixed(0);
                  return (
                    <div key={t.tier} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: t.color }} />
                        <span className="text-slate-300">{t.label}</span>
                      </div>
                      <span className="text-white font-medium">
                        {isRevenue ? formatCurrency(val) : val}
                        <span className="text-slate-500 ml-1">({pct}%)</span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* AUM Bucket - Bar */}
      <Card className="bg-slate-800/50 border-white/10">
        <CardContent className="p-3">
          <p className="text-xs text-slate-400 mb-2">{isRevenue ? 'Revenue by AUM Bucket' : 'By AUM Bucket'}</p>
          {aumBuckets.length === 0 || aumBuckets.every(b => (b[aumKey] || 0) === 0) ? (
            <p className="text-center text-slate-500 py-4 text-xs">No AUM data</p>
          ) : (
            <div className="h-[120px]" data-testid="period-aum-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={aumBuckets} margin={{ top: 5, right: 5, bottom: 5, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="label" stroke="#94a3b8" tick={{ fontSize: 10 }} />
                  <YAxis stroke="#94a3b8" tick={{ fontSize: 10 }} allowDecimals={false} tickFormatter={fmtY} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', fontSize: 12 }}
                    formatter={(v) => fmtTip(v)}
                  />
                  <Bar dataKey={aumKey} fill={primaryColor} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Breakdown by Dealer/RM - Horizontal Bar */}
      {breakdown.length > 0 && (
        <Card className="bg-slate-800/50 border-white/10">
          <CardContent className="p-3">
            <p className="text-xs text-slate-400 mb-2">
              {breakdownLabel || 'Breakdown'}{isRevenue ? ' (Revenue)' : ''}
            </p>
            <div className="h-[130px]" data-testid="period-breakdown-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={breakdown.slice(0, 10)} layout="vertical" margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis type="number" stroke="#94a3b8" tick={{ fontSize: 10 }} allowDecimals={false} tickFormatter={fmtY} />
                  <YAxis type="category" dataKey="label" stroke="#94a3b8" tick={{ fontSize: 10 }} width={80} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', fontSize: 12 }}
                    formatter={(v) => fmtTip(v)}
                  />
                  <Bar dataKey={breakdownKey} fill="#C5A059" radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
};





const DashboardPage = () => {
  const { user, token } = useAuth();
  const [stats, setStats] = useState(null);
  const [aumData, setAumData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showActiveClientsDialog, setShowActiveClientsDialog] = useState(false);
  const [showRevenueDetailsDialog, setShowRevenueDetailsDialog] = useState(false);
  const [revenueDetails, setRevenueDetails] = useState(null);
  const [revenueDetailsLoading, setRevenueDetailsLoading] = useState(false);
  const [selectedDateDetails, setSelectedDateDetails] = useState(null);
  const [dateDetailsLoading, setDateDetailsLoading] = useState(false);
  const [timeFilter, setTimeFilter] = useState('this_month');
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  
  // Custom date range state
  const [customRange, setCustomRange] = useState({ from: null, to: null });
  const [showCustomRangePicker, setShowCustomRangePicker] = useState(false);
  const [tempCustomRange, setTempCustomRange] = useState({ from: null, to: null });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Fetch stats when time filter or custom range changes
  useEffect(() => {
    if (!loading) {
      if (timeFilter === 'custom' && customRange.from && customRange.to) {
        fetchStatsWithCustomRange(customRange);
      } else if (timeFilter !== 'custom') {
        fetchStatsWithFilter(timeFilter);
      }
    }
  }, [timeFilter, customRange]);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, aumRes] = await Promise.all([
        axios.get(`${API_URL}/dashboard/stats?time_filter=${timeFilter}`),
        axios.get(`${API_URL}/dashboard/charts/monthly-aum`)
      ]);
      
      setStats(statsRes.data);
      setAumData(aumRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatsWithFilter = async (filter) => {
    setStatsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/dashboard/stats?time_filter=${filter}`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch filtered stats:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchStatsWithCustomRange = async (range) => {
    setStatsLoading(true);
    try {
      const fromDate = new Date(range.from).toISOString().split('T')[0];
      const toDate = new Date(range.to).toISOString().split('T')[0];
      const response = await axios.get(`${API_URL}/dashboard/stats?time_filter=custom&date_from=${fromDate}&date_to=${toDate}`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch custom range stats:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleFilterChange = (filter) => {
    if (filter === 'custom') {
      // Open custom range picker
      setTempCustomRange(customRange.from ? customRange : { from: new Date(), to: new Date() });
      setShowCustomRangePicker(true);
      setFilterDropdownOpen(false);
    } else {
      setTimeFilter(filter);
      setFilterDropdownOpen(false);
    }
  };

  const handleCustomRangeApply = () => {
    if (tempCustomRange.from && tempCustomRange.to) {
      setCustomRange(tempCustomRange);
      setTimeFilter('custom');
      setShowCustomRangePicker(false);
    }
  };

  // Fetch revenue details for day-wise breakdown
  const fetchRevenueDetails = async (period) => {
    setRevenueDetailsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/dashboard/revenue-details`, {
        params: { period },
        headers: { Authorization: `Bearer ${token}` }
      });
      setRevenueDetails(response.data);
    } catch (error) {
      console.error('Error fetching revenue details:', error);
    } finally {
      setRevenueDetailsLoading(false);
    }
  };

  // Fetch client-wise breakdown for a specific date
  const fetchDateDetails = async (date) => {
    setDateDetailsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/dashboard/revenue-details/${date}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedDateDetails(response.data);
    } catch (error) {
      console.error('Error fetching date details:', error);
    } finally {
      setDateDetailsLoading(false);
    }
  };

  // Handle revenue card click
  const handleRevenueCardClick = () => {
    setShowRevenueDetailsDialog(true);
    setSelectedDateDetails(null);
    fetchRevenueDetails(timeFilter);
  };

  // Export revenue data to CSV
  const exportRevenueToCSV = () => {
    if (!revenueDetails?.daily_data) return;
    
    const headers = ['Date', 'Brokerage (₹)', 'Clients', 'Top Client', 'Top Client Brokerage (₹)'];
    const rows = revenueDetails.daily_data.map(d => [
      d.date,
      d.brokerage,
      d.client_count,
      d.top_client_name || '',
      d.top_client_brokerage
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `revenue_${revenueDetails.period}_${revenueDetails.date_start}_to_${revenueDetails.date_end}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Export client details to CSV
  const exportDateDetailsToCSV = () => {
    if (!selectedDateDetails?.clients) return;
    
    const headers = ['Client Code', 'Name', 'Contact', 'Segment', 'Brokerage (₹)', 'RM', 'Dealer'];
    const rows = selectedDateDetails.clients.map(c => [
      c.client_code,
      c.name,
      c.contact_number || '',
      c.segment || '',
      c.brokerage,
      c.rm_name || '',
      c.dealer_name || ''
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `revenue_clients_${selectedDateDetails.date}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const formatDateRange = (from, to) => {
    if (!from || !to) return 'Select dates';
    const fromStr = new Date(from).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    const toStr = new Date(to).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    return `${fromStr} - ${toStr}`;
  };

  // Get current filter label
  const currentFilterLabel = timeFilter === 'custom' && customRange.from && customRange.to
    ? formatDateRange(customRange.from, customRange.to)
    : TIME_FILTERS.find(f => f.value === timeFilter)?.label || 'This Month';
  
  // Get dynamic widget labels
  const widgetLabels = getWidgetLabels(timeFilter, customRange);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin w-8 h-8 border-2 border-[#C5A059] border-t-transparent rounded-full" />
      </div>
    );
  }

  // Role display logic based on user's requirement:
  // - If user has 'rm' in roles (regardless of dealer) → "Relationship Manager"
  // - If user has only 'dealer' → "Dealer"
  // - Other roles: Business Head, Back Office, Principal (Super Admin)
  const getRoleDisplayLabel = () => {
    const roles = user?.roles || [user?.role];
    const primaryRole = user?.role;
    
    // Check if user has RM role (takes precedence over dealer)
    if (roles.includes('rm')) {
      return 'Relationship Manager';
    }
    // Check if user is dealer only (no RM role)
    if (roles.includes('dealer')) {
      return 'Dealer';
    }
    // Other roles
    const roleLabels = {
      principal: 'Principal',
      business_head: 'Business Head',
      backoffice: 'Back Office'
    };
    return roleLabels[primaryRole] || primaryRole;
  };

  // Legacy roleLabel object for backward compatibility with other components
  const roleLabel = {
    principal: 'Principal',
    business_head: 'Business Head',
    dealer: 'Dealer/RM',
    rm: 'Relationship Manager',
    backoffice: 'Back Office'
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      {/* Header with Time Filter */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8 gap-4">
        <div>
          <h1 className="font-heading text-3xl lg:text-4xl font-bold text-white tracking-tight">
            Welcome back, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-slate-400 mt-2">
            <span className="text-[#C5A059] font-medium">{getRoleDisplayLabel()}</span> Dashboard • {new Date().toLocaleDateString('en-IN', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
        </div>
        
        {/* Time Filter Dropdown */}
        <div className="relative" data-testid="time-filter-dropdown">
          <button
            onClick={() => setFilterDropdownOpen(!filterDropdownOpen)}
            className={`flex items-center gap-2 px-4 py-2.5 bg-slate-800/80 border border-[#C5A059]/30 rounded-lg text-white hover:border-[#C5A059]/60 transition-all ${timeFilter === 'custom' ? 'min-w-[280px]' : 'min-w-[160px]'}`}
          >
            <CalendarIcon className="w-4 h-4 text-[#C5A059]" />
            <span className="font-medium truncate">{currentFilterLabel}</span>
            <ChevronDown className={`w-4 h-4 text-slate-400 ml-auto flex-shrink-0 transition-transform ${filterDropdownOpen ? 'rotate-180' : ''}`} />
          </button>
          
          {filterDropdownOpen && (
            <div className="absolute right-0 mt-2 w-56 bg-slate-800 border border-[#C5A059]/30 rounded-lg shadow-xl z-50 overflow-hidden">
              {TIME_FILTERS.map((filter) => (
                <button
                  key={filter.value}
                  onClick={() => handleFilterChange(filter.value)}
                  className={`w-full px-4 py-2.5 text-left hover:bg-[#C5A059]/10 transition-colors flex items-center gap-2 ${
                    timeFilter === filter.value 
                      ? 'bg-[#C5A059]/20 text-[#C5A059] border-l-2 border-[#C5A059]' 
                      : 'text-slate-300'
                  }`}
                  data-testid={`filter-${filter.value}`}
                >
                  {filter.value === 'custom' && <CalendarIcon className="w-4 h-4" />}
                  {filter.label}
                  {timeFilter === filter.value && filter.value !== 'custom' && (
                    <span className="ml-auto text-xs bg-[#C5A059]/30 px-1.5 py-0.5 rounded">Active</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Custom Date Range Picker Dialog */}
      <Dialog open={showCustomRangePicker} onOpenChange={setShowCustomRangePicker}>
        <DialogContent className="max-w-fit bg-[#1a1a2e] border-[#C5A059]/20">
          <DialogHeader>
            <DialogTitle className="text-xl text-white flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-[#C5A059]" />
              Select Date Range
            </DialogTitle>
          </DialogHeader>
          
          <div className="p-4">
            <div className="flex gap-4 justify-center">
              {/* From Date */}
              <div className="space-y-2">
                <label className="text-sm text-slate-400">From Date</label>
                <Calendar
                  mode="single"
                  selected={tempCustomRange.from}
                  onSelect={(date) => setTempCustomRange(prev => ({ ...prev, from: date }))}
                  disabled={(date) => date > new Date() || (tempCustomRange.to && date > tempCustomRange.to)}
                  className="rounded-md border border-[#C5A059]/20 bg-slate-800/50"
                />
              </div>
              
              {/* To Date */}
              <div className="space-y-2">
                <label className="text-sm text-slate-400">To Date</label>
                <Calendar
                  mode="single"
                  selected={tempCustomRange.to}
                  onSelect={(date) => setTempCustomRange(prev => ({ ...prev, to: date }))}
                  disabled={(date) => date > new Date() || (tempCustomRange.from && date < tempCustomRange.from)}
                  className="rounded-md border border-[#C5A059]/20 bg-slate-800/50"
                />
              </div>
            </div>
            
            {/* Selected Range Preview */}
            <div className="mt-4 p-3 bg-slate-800/50 rounded-lg border border-[#C5A059]/20">
              <p className="text-sm text-slate-400 mb-1">Selected Range:</p>
              <p className="text-white font-medium">
                {tempCustomRange.from && tempCustomRange.to 
                  ? formatDateRange(tempCustomRange.from, tempCustomRange.to)
                  : 'Please select both dates'}
              </p>
            </div>
            
            {/* Action Buttons */}
            <div className="flex gap-3 mt-4 justify-end">
              <Button
                variant="outline"
                onClick={() => setShowCustomRangePicker(false)}
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCustomRangeApply}
                disabled={!tempCustomRange.from || !tempCustomRange.to}
                className="bg-[#C5A059] text-black hover:bg-[#D4AF6A]"
              >
                Apply Range
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Stats Grid with Loading Overlay */}
      <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 stagger-children transition-opacity ${statsLoading ? 'opacity-60' : ''}`}>
        {/* AUM - Always shows latest snapshot, no filter applied */}
        <StatCard
          title={widgetLabels.aum}
          value={formatExactCurrency(stats?.total_aum || 0)}
          icon={Wallet}
          color="gold"
        />
        {/* New Clients - Filtered by created_at */}
        <StatCard
          title={widgetLabels.clients}
          value={stats?.total_clients || 0}
          icon={Users}
          trend={stats?.total_clients > 0 ? "up" : undefined}
          trendValue={stats?.total_clients_all ? `of ${stats.total_clients_all} total` : undefined}
          color="blue"
        />
        {/* Revenue - Filtered by trade_date with Target */}
        <div 
          className="cursor-pointer transition-all hover:scale-[1.02]"
          onClick={handleRevenueCardClick}
          data-testid="revenue-card-clickable"
        >
          <Card className="card-vault stat-card h-full border-emerald-500/30 hover:border-emerald-500/60" data-testid="revenue-card">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="label-uppercase mb-2">{widgetLabels.revenue}</p>
                  <p className="font-heading text-2xl lg:text-3xl font-bold text-emerald-500 tabular-nums">
                    {formatExactCurrency(stats?.monthly_revenue || 0)}
                    {stats?.revenue_target > 0 && (
                      <span className="text-lg text-slate-400 font-normal">/{formatExactCurrency(stats?.revenue_target || 0)}</span>
                    )}
                  </p>
                  {stats?.revenue_target > 0 && (
                    <div className="flex items-center mt-2 text-sm text-slate-400">
                      <div className="w-full bg-slate-700 rounded-full h-1.5 mr-2 max-w-[100px]">
                        <div 
                          className={`h-1.5 rounded-full ${stats?.revenue_percent >= 100 ? 'bg-emerald-500' : stats?.revenue_percent >= 75 ? 'bg-yellow-500' : 'bg-red-500'}`}
                          style={{ width: `${Math.min(stats?.revenue_percent || 0, 100)}%` }}
                        />
                      </div>
                      <span>{stats?.revenue_percent || 0}% of target</span>
                    </div>
                  )}
                  {/* Show pro-rated target for YTD view */}
                  {timeFilter === 'this_year' && stats?.prorated_target > 0 && (
                    <div className="mt-2 text-xs text-slate-500">
                      <span>Pro-rated target till today: </span>
                      <span className="text-slate-400">{formatExactCurrency(stats?.prorated_target || 0)}</span>
                    </div>
                  )}
                  <p className="text-xs text-slate-500 mt-1">Click for details →</p>
                </div>
                <div className="p-3 rounded-lg bg-emerald-500/10">
                  <TrendingUp className="w-6 h-6 text-emerald-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        {/* Active Clients - Filtered by trade_date */}
        <div 
          className="cursor-pointer transition-all hover:scale-[1.02]"
          onClick={() => setShowActiveClientsDialog(true)}
          data-testid="active-clients-widget"
        >
          <Card className="card-vault stat-card h-full border-emerald-500/30 hover:border-emerald-500/60">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="label-uppercase mb-2">{widgetLabels.active}</p>
                  <p className="font-heading text-2xl lg:text-3xl font-bold text-emerald-500 tabular-nums">
                    {stats?.active_clients || 0}<span className="text-lg text-slate-400 font-normal">/{stats?.total_clients_all || 0}</span>
                  </p>
                  <div className="flex items-center mt-2 text-sm text-slate-400">
                    <span>{stats?.active_clients_percent || 0}% of total clients</span>
                  </div>
                </div>
                <div className="p-3 rounded-lg bg-emerald-500/10">
                  <TrendingUp className="w-6 h-6 text-emerald-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        {/* KYC Verified - Filtered by kyc_verified_date */}
        <StatCard
          title={widgetLabels.kyc}
          value={`${stats?.verified_kyc || 0}`}
          icon={ShieldCheck}
          color="gold"
        />
      </div>

      {/* Loading indicator for stats refresh */}
      {statsLoading && (
        <div className="fixed top-4 right-4 bg-slate-800/90 px-4 py-2 rounded-lg flex items-center gap-2 z-50 border border-[#C5A059]/30">
          <div className="animate-spin w-4 h-4 border-2 border-[#C5A059] border-t-transparent rounded-full" />
          <span className="text-sm text-slate-300">Updating...</span>
        </div>
      )}

      {/* Active Clients Dialog */}
      <ActiveClientsDialog 
        open={showActiveClientsDialog} 
        onClose={() => setShowActiveClientsDialog(false)}
        token={token}
        timeFilter={timeFilter}
        customRange={customRange}
      />

      {/* Revenue Details Dialog */}
      <Dialog open={showRevenueDetailsDialog} onOpenChange={setShowRevenueDetailsDialog}>
        <DialogContent className="max-w-[95vw] w-[1400px] max-h-[95vh] overflow-hidden flex flex-col bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="font-heading text-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-emerald-500" />
                {selectedDateDetails ? (
                  <span>Client Details - {selectedDateDetails.date}</span>
                ) : (
                  <span>Revenue Details - {revenueDetails?.period_label || 'Loading...'}</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {!selectedDateDetails && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={exportRevenueToCSV}
                    className="text-xs mr-8"
                    disabled={!revenueDetails?.daily_data}
                    data-testid="revenue-export-csv"
                  >
                    📥 Export CSV
                  </Button>
                )}
                {selectedDateDetails && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={exportDateDetailsToCSV}
                      className="text-xs"
                    >
                      📥 Export CSV
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedDateDetails(null)}
                      className="text-xs mr-8"
                    >
                      ← Back to Day-wise
                    </Button>
                  </>
                )}
              </div>
            </DialogTitle>
          </DialogHeader>
          
          {revenueDetailsLoading || dateDetailsLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full" />
            </div>
          ) : selectedDateDetails ? (
            /* Client-wise breakdown for selected date */
            <div className="space-y-4 overflow-auto">
              <div className="grid grid-cols-2 gap-4">
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="p-4">
                    <p className="text-sm text-slate-400">Total Clients</p>
                    <p className="text-2xl font-bold text-white">{selectedDateDetails.summary?.total_clients || 0}</p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="p-4">
                    <p className="text-sm text-slate-400">Total Brokerage</p>
                    <p className="text-2xl font-bold text-emerald-500">{formatCurrency(selectedDateDetails.summary?.total_brokerage || 0)}</p>
                  </CardContent>
                </Card>
              </div>
              
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700">
                    <TableHead className="text-slate-400">Client Code</TableHead>
                    <TableHead className="text-slate-400">Name</TableHead>
                    <TableHead className="text-slate-400">Contact</TableHead>
                    <TableHead className="text-slate-400">Segment</TableHead>
                    <TableHead className="text-slate-400">RM</TableHead>
                    <TableHead className="text-slate-400">Dealer</TableHead>
                    <TableHead className="text-slate-400 text-right">Brokerage</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedDateDetails.clients?.map((client, idx) => (
                    <TableRow key={idx} className="border-slate-700 hover:bg-slate-800/50">
                      <TableCell className="font-mono text-white">{client.client_code}</TableCell>
                      <TableCell className="text-white">{client.name}</TableCell>
                      <TableCell>
                        {client.contact_number ? (
                          <ClickToCall
                            phoneNumber={client.contact_number}
                            clientCode={client.client_code}
                            clientName={client.name}
                            token={token}
                          />
                        ) : (
                          <span className="text-slate-500">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-slate-300">{client.segment || '-'}</TableCell>
                      <TableCell className="text-slate-300">{client.rm_name || '-'}</TableCell>
                      <TableCell className="text-slate-300">{client.dealer_name || '-'}</TableCell>
                      <TableCell className="text-right font-mono text-emerald-400">{formatCurrency(client.brokerage)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : revenueDetails ? (
            /* Day-wise breakdown — Summary on top, then 60/40 Table + Charts */
            <div className="flex-1 overflow-hidden flex flex-col gap-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-4 gap-4">
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="p-4">
                    <p className="text-sm text-slate-400">Total Revenue</p>
                    <p className="text-2xl font-bold text-emerald-500">{formatExactCurrency(revenueDetails.summary?.total_revenue || 0)}</p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="p-4">
                    <p className="text-sm text-slate-400">Trading Days</p>
                    <p className="text-2xl font-bold text-white">{revenueDetails.summary?.total_days || 0}</p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="p-4">
                    <p className="text-sm text-slate-400">Unique Clients</p>
                    <p className="text-2xl font-bold text-white">{revenueDetails.summary?.total_unique_clients || 0}</p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="p-4">
                    <p className="text-sm text-slate-400">Avg. Daily Revenue</p>
                    <p className="text-2xl font-bold text-sky-400">{formatExactCurrency(revenueDetails.summary?.avg_daily_revenue || 0)}</p>
                  </CardContent>
                </Card>
              </div>

              {/* 60/40 Side-by-side Table + Charts */}
              <div className="flex-1 overflow-hidden flex gap-4">
                {/* TABLE 60% */}
                <div className="w-3/5 overflow-auto pr-2" data-testid="revenue-table-column">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-700">
                        <TableHead className="text-slate-400">Date</TableHead>
                        <TableHead className="text-slate-400 text-right">Brokerage</TableHead>
                        <TableHead className="text-slate-400 text-center">Clients</TableHead>
                        <TableHead className="text-slate-400">Top Client</TableHead>
                        <TableHead className="text-slate-400 text-right">Top Brokerage</TableHead>
                        <TableHead className="text-slate-400 text-center">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {revenueDetails.daily_data?.map((day, idx) => (
                        <TableRow key={idx} className="border-slate-700 hover:bg-slate-800/50">
                          <TableCell className="font-medium text-white">
                            {new Date(day.date).toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}
                          </TableCell>
                          <TableCell className="text-right font-mono text-emerald-400">{formatCurrency(day.brokerage)}</TableCell>
                          <TableCell className="text-center text-white">{day.client_count}</TableCell>
                          <TableCell className="text-slate-300">{day.top_client_name || '-'}</TableCell>
                          <TableCell className="text-right font-mono text-sky-400">{formatCurrency(day.top_client_brokerage)}</TableCell>
                          <TableCell className="text-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => fetchDateDetails(day.date)}
                              className="text-xs text-emerald-400 hover:text-emerald-300"
                            >
                              View Clients →
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {/* Total Row */}
                      <TableRow className="border-slate-700 bg-slate-800/50 font-bold">
                        <TableCell className="text-white">TOTAL</TableCell>
                        <TableCell className="text-right font-mono text-emerald-400">{formatCurrency(revenueDetails.summary?.total_revenue || 0)}</TableCell>
                        <TableCell className="text-center text-white">{revenueDetails.summary?.total_unique_clients || 0}</TableCell>
                        <TableCell colSpan={3}></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
                {/* CHARTS 40% */}
                <div className="w-2/5 overflow-auto pl-2 border-l border-white/5 space-y-3" data-testid="revenue-charts-column">
                  <PeriodChartsPanel
                    dailyTrend={revenueDetails.chart_data || []}
                    tierDistribution={revenueDetails.tier_distribution || []}
                    aumBuckets={revenueDetails.aum_buckets || []}
                    breakdown={revenueDetails.breakdown || []}
                    breakdownLabel={revenueDetails.breakdown_label || null}
                    metric="revenue"
                    primaryColor="#10b981"
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">No data available</div>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== RM & DEALER DASHBOARD WIDGETS (Ordered) ===== */}
      {token && (user?.role === 'dealer' || user?.role === 'rm') && (
        <>
          {/* 5️⃣ Focus Advisory Snapshot (includes Brokerage Gap with expandable chart) */}
          <FocusSnapshotWidget />

          {/* 6️⃣ Latest Ledger Balances (Snapshot) */}
          <LedgerBalanceWidget token={token} />

          {/* 7️⃣ Dormant Clients Overview */}
          <DormantClientsWidget token={token} />

          {/* 8️⃣ My Clients by Tier */}
          <DealerTierTabsWidget token={token} />

          {/* 9️⃣ Pending Actions */}
          <Card className="card-vault" data-testid="pending-actions">
            <CardHeader className="border-b border-white/5">
              <CardTitle className="font-heading text-white flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-amber-500" />
                Pending Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-3">
                {stats?.pending_kyc > 0 && (
                  <div className="flex items-center justify-between p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                    <div className="flex items-center gap-3">
                      <ShieldCheck className="w-5 h-5 text-amber-500" />
                      <span className="text-slate-300">{stats.pending_kyc} clients pending KYC verification</span>
                    </div>
                    <span className="badge-warning text-xs px-2 py-1 rounded-full">Pending</span>
                  </div>
                )}
                {stats?.pending_kyc === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    <ShieldCheck className="w-12 h-12 mx-auto mb-2 text-emerald-500" />
                    <p>All clients verified!</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* 🔟 Pending Activations (Dormant 3 - Awaiting Brokerage) */}
          <PendingActivationsWidget token={token} />

          {/* 1️⃣1️⃣ My Incentives */}
          <MyIncentivesWidget />

          {/* 1️⃣1️⃣ AUM Growth Trend */}
          <Card className="card-vault" data-testid="aum-trend-chart">
            <CardHeader className="border-b border-white/5">
              <CardTitle className="font-heading text-white flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-[#C5A059]" />
                AUM Growth Trend
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={aumData}>
                    <defs>
                      <linearGradient id="aumGradientRM" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#C5A059" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#C5A059" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis 
                      dataKey="month" 
                      stroke="#64748b" 
                      fontSize={12}
                      tickLine={false}
                    />
                    <YAxis 
                      stroke="#64748b" 
                      fontSize={12}
                      tickLine={false}
                      tickFormatter={(value) => formatCurrency(value)}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Area 
                      type="monotone" 
                      dataKey="aum" 
                      stroke="#C5A059" 
                      strokeWidth={2}
                      fill="url(#aumGradientRM)"
                      name="AUM"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* ===== PRINCIPAL / BUSINESS HEAD DASHBOARD WIDGETS ===== */}
      {token && (user?.role === 'principal' || user?.role === 'business_head') && (
        <>
          {/* 5️⃣ Firm Brokerage Gap Overview */}
          <PrincipalBrokerageGapWidget />

          {/* 6️⃣ Brokerage Activation Focus – Firm Overview */}
          <PrincipalFocusClientsWidget token={token} />

          {/* 7️⃣ Dormant Clients Overview */}
          <DormantClientsWidget token={token} />

          {/* 8️⃣ Latest Ledger Balances (Snapshot) */}
          <LedgerBalanceWidget token={token} />

          {/* 9️⃣ Pending Actions */}
          <Card className="card-vault" data-testid="pending-actions-principal">
            <CardHeader className="border-b border-white/5 py-3">
              <CardTitle className="font-heading text-white flex items-center gap-2 text-base">
                <AlertCircle className="w-5 h-5 text-amber-500" />
                Pending Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="space-y-2">
                {stats?.pending_kyc > 0 && (
                  <div className="flex items-center justify-between p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                    <div className="flex items-center gap-3">
                      <ShieldCheck className="w-5 h-5 text-amber-500" />
                      <span className="text-slate-300">{stats.pending_kyc} clients pending KYC verification</span>
                    </div>
                    <span className="badge-warning text-xs px-2 py-1 rounded-full">Pending</span>
                  </div>
                )}
                {stats?.pending_kyc === 0 && (
                  <div className="text-center py-4 text-slate-500">
                    <ShieldCheck className="w-10 h-10 mx-auto mb-2 text-emerald-500" />
                    <p className="text-sm">All clients verified!</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* 🔟 Client Tier Overview (Compact) */}
          <TierOverviewWidget token={token} compact={true} />

          {/* 1️⃣1️⃣ Incentives Overview (Compact) */}
          <IncentivesSummaryWidget compact={true} />

          {/* 1️⃣2️⃣ AUM Growth Trend */}
          <Card className="card-vault" data-testid="aum-trend-chart-principal">
            <CardHeader className="border-b border-white/5 py-3">
              <CardTitle className="font-heading text-white flex items-center gap-2 text-base">
                <TrendingUp className="w-5 h-5 text-[#C5A059]" />
                AUM Growth Trend
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={aumData}>
                    <defs>
                      <linearGradient id="aumGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#C5A059" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#C5A059" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis 
                      dataKey="month" 
                      stroke="#64748b" 
                      fontSize={11}
                      tickLine={false}
                    />
                    <YAxis 
                      stroke="#64748b" 
                      fontSize={11}
                      tickLine={false}
                      tickFormatter={(value) => formatCurrency(value)}
                      width={70}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Area 
                      type="monotone" 
                      dataKey="aum" 
                      stroke="#C5A059" 
                      strokeWidth={2}
                      fill="url(#aumGradient)"
                      name="AUM"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default DashboardPage;
